import React, { useEffect, useMemo, useState } from 'react';

import { ASSETS, FIREBASE_CONFIG } from '../../config/Config';
import {
  watchLegacyTriage,
  watchAppTriage,
  watchUnreadAlerts,
} from '../../services/MasterSidebarRepository.js';
import { BarChart3, CalendarClock, MonitorPlay, Wrench } from 'lucide-react';
import { useFinancialLedger } from '../../hooks/useFinancialLedger';
import { getOverdueEntries } from '../../utils/financialAnalytics';

const appId = FIREBASE_CONFIG.projectId;

const AccordionSection = ({ id, title, iconClass, open, onToggle, children, accent }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      data-testid={`sidebar-section-${id}`}
      aria-expanded={open ? 'true' : 'false'}
      aria-controls={`${id}-panel`}
      className="w-full px-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]/60 focus-visible:ring-offset-0"
    >
      <div className={`w-full flex items-center justify-between text-xs font-semibold uppercase tracking-widest mb-2 px-0 ${accent ? 'text-[#C5A059]' : 'text-gray-400'}`}>
        <span className="flex items-center gap-2 min-w-0">
          {iconClass ? <i className={`${iconClass} w-3.5 text-center`} aria-hidden="true"></i> : null}
          <span className="truncate">{title}</span>
        </span>
        <i
          className={`fas fa-chevron-down text-[10px] ${open ? '' : '-rotate-90'}`}
          aria-hidden="true"
        ></i>
      </div>
    </button>

    <div
      id={`${id}-panel`}
      role="region"
      aria-hidden={open ? 'false' : 'true'}
      className={
        open
          ? `flex flex-col pl-2 ml-3 border-l-2 ${accent ? 'border-[#C5A059]/20' : 'border-white/20'}`
          : 'hidden'
      }
    >
      {children}
    </div>
  </div>
);

const MenuItem = ({ id, label, iconClass, icon: Icon, active, onClick, to, isExternal, nested, rightIconClass, badgeCount }) => {
  const className = `group w-full text-left px-3 py-2 rounded-md flex items-center gap-3 relative overflow-hidden ${
    active
    ? 'bg-[#C5A059] text-[#20243B] shadow-md font-semibold'
    : 'text-gray-400 hover:bg-white/5 hover:text-white'
  }`;

  const inner = (
    <>
      {Icon ? (
        <Icon
          className={`w-3.5 h-3.5 ${active ? 'text-[#20243B]' : 'text-gray-400 group-hover:text-[#C5A059]'}`}
          aria-hidden="true"
        />
      ) : null}
      {iconClass ? (
        <i
          className={`${iconClass} w-3.5 text-center ${active ? 'text-[#20243B]' : 'text-gray-400 group-hover:text-[#C5A059]'}`}
          aria-hidden="true"
        ></i>
      ) : null}
      <span className="text-xs font-medium truncate flex-1">{label}</span>

      {Number(badgeCount || 0) > 0 ? (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-extrabold">
          {Number(badgeCount) > 99 ? '99+' : Number(badgeCount)}
        </span>
      ) : null}

      {rightIconClass ? (
        <i className={`${rightIconClass} text-[10px] opacity-70 ${active ? 'text-[#20243B]' : 'text-gray-400 group-hover:text-[#C5A059]'}`} aria-hidden="true"></i>
      ) : (
        (active && !isExternal) ? <i className="fas fa-chevron-right text-[10px] opacity-50 text-[#20243B]" aria-hidden="true"></i> : null
      )}
    </>
  );

  // Itens navegáveis (com `to`) viram <a href> de verdade — permite abrir em
  // nova guia (Ctrl/Cmd+clique, clique do meio, "Abrir em nova guia" do menu
  // do navegador). Clique normal continua chamando onClick(id) exatamente
  // como antes (SPA, sem recarregar a página); só o clique modificado deixa
  // o navegador seguir o href nativamente, sem mudar a guia atual.
  if (to) {
    return (
      <a
        href={to}
        data-testid={`sidebar-${id}`}
        className={className}
        onClick={(e) => {
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onClick(id);
        }}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={`sidebar-${id}`}
      className={className}
    >
      {inner}
    </button>
  );
};

export const MasterSidebar = ({ activeTab, user, logoutUser, onNavigate }) => {
  const isDev = Boolean(typeof import.meta !== 'undefined' && import.meta?.env && import.meta.env.DEV);
  const [portalInboxCount, setPortalInboxCount] = useState(0);
  const [alertInboxCount, setAlertInboxCount] = useState(0);
  const { ledger: financialLedger } = useFinancialLedger();
  const overdueCount = getOverdueEntries(financialLedger).length;
  const sections = useMemo(
    () => [
      {
        key: 'advocacia',
        title: 'Gestão do Escritório',
        iconClass: 'fas fa-briefcase',
        accent: false,
        items: [
          { id: 'home', label: 'Visão Geral', iconClass: 'fas fa-chart-pie' },
          {
            id: 'documents',
            label: 'Documentos',
            iconClass: 'fas fa-file-alt',
            subItems: [
              { id: 'file_hub', label: 'Processos e Casos', iconClass: 'fas fa-folder-open' },
              // { id: 'alert_inbox', label: 'Central de Alertas', iconClass: 'fas fa-satellite-dish' }, // OCULTO 20/05/2026 — fora de contexto no submenu Documentos
              // { id: 'whatsapp_forensics', label: 'Perícia WhatsApp', iconClass: 'fab fa-whatsapp' }, // OCULTO 19/05/2026 — substituído pelo pipeline CLI v2.0 (export_chat_completo.py)
              { id: 'contracts', label: 'Contratos', iconClass: 'fas fa-file-contract' },
              { id: 'procurations', label: 'Procurações', iconClass: 'fas fa-file-signature' },
              { id: 'declarations', label: 'Declarações', iconClass: 'fas fa-file-alt' },
              { id: 'termo_renuncia', label: 'Termo de Renúncia', iconClass: 'fas fa-user-slash' },
              { id: 'termo_revogacao', label: 'Termo de Revogação', iconClass: 'fas fa-handshake-slash' },
              // { id: 'petitions', label: 'Petições', iconClass: 'fas fa-gavel' }, // OCULTO 31/05/2026 — redação de petições ocorre no Word + RDA.Studio
              { id: 'informed_consents', label: 'Consentimentos', iconClass: 'fas fa-shield-alt' },
            ],
          },
          { id: 'clientes', label: 'Clientes', iconClass: 'fas fa-users' },
          { id: 'prontuario_global', label: 'Prontuário Global', iconClass: 'fas fa-clipboard-list' },
          { id: 'agenda', label: 'Agenda & Prazos', iconClass: 'fas fa-calendar-alt' },
          { id: 'deadline_calculator', label: 'Calculadora de Prazos', icon: CalendarClock },
          { id: 'timesheet', label: 'Timesheet', iconClass: 'fas fa-clock' },
        ],
      },
      {
        key: 'academia',
        title: 'Academia RDA',
        iconClass: 'fas fa-graduation-cap',
        accent: true,
        items: [
          { id: 'academy_studio', label: 'Estúdio', icon: MonitorPlay },
          { id: 'academy_research', label: 'Biblioteca Digital', iconClass: 'fas fa-search' },
          { id: 'academy_admin', label: 'Gestão de Ensino', iconClass: 'fas fa-chalkboard-teacher' },
        ],
      },
      {
        key: 'financeiro',
        title: 'Financeiro',
        iconClass: 'fas fa-coins',
        accent: false,
        items: [
          { id: 'financial_manager', label: 'Painel Geral', iconClass: 'fas fa-landmark' },
          { id: 'financeiro', label: 'Escritório', iconClass: 'fas fa-coins' },
          { id: 'personal_finance', label: 'Pessoal', iconClass: 'fas fa-piggy-bank' },
          { id: 'recta_finance', label: 'Recta Scripta', iconClass: 'fas fa-print' },
          { id: 'legal_bi', label: 'Legal B.I.', icon: BarChart3 },
          { id: 'investments', label: 'Projeto Milhão', iconClass: 'fas fa-rocket' },
        ],
      },
      {
        key: 'admin',
        title: 'Administração',
        iconClass: 'fas fa-tools',
        accent: false,
        items: [
          { id: 'surgeon_hub', label: 'Cirurgia RDA', icon: Wrench },
        ],
      },
    ],
    []
  );

  useEffect(() => {
    let unsub = null;
    let mounted = true;

    unsub = watchLegacyTriage(
      () => {},
      (e) => {
        try {
          unsub && unsub();
        } catch (err) {
          void err;
        }
        const hasAppId = String(appId || '').trim();
        if (!hasAppId) {
          setPortalInboxCount(0);
          return;
        }
        unsub = watchAppTriage(
          hasAppId,
          () => setPortalInboxCount(0),
          () => setPortalInboxCount(0)
        );
      }
    );

    return () => {
      mounted = false;
      try {
        unsub && unsub();
      } catch (e) {
        void e;
      }
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    let unsubAlerts = null;
    try {
      unsubAlerts = watchUnreadAlerts(
        (snap) => { if (mounted) setAlertInboxCount(snap.size); },
        () => { if (mounted) setAlertInboxCount(0); },
      );
    } catch {
      setAlertInboxCount(0);
    }
    return () => {
      mounted = false;
      try { unsubAlerts && unsubAlerts(); } catch { void 0; }
    };
  }, []);

  const initialOpen = useMemo(() => {
    const map = { advocacia: true, academia: true, financeiro: false };
    return map;
  }, []);

  const [openSections, setOpenSections] = useState(initialOpen);
  const [openItemGroups, setOpenItemGroups] = useState({ documents: true });
  const [chaosRunning, setChaosRunning] = useState(false);
  const [chaosReportsOpen, setChaosReportsOpen] = useState(false);
  const [chaosReports, setChaosReports] = useState([]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleItemGroup = (key) => {
    setOpenItemGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <aside data-testid="master-sidebar" className="rda-sidebar w-64 bg-[#1a1e30] text-white flex flex-col h-full flex-shrink-0 print:hidden z-30 shadow-2xl border-r border-gray-800">
      
      {/* HEADER LOGO */}
      <div className="h-12 flex items-center px-4 border-b border-gray-700/50 bg-[#20243B] flex-shrink-0">
        <button
          type="button"
          onClick={() => setActiveTab('home')}
          data-testid="sidebar-home-logo"
          className="flex items-center gap-2 md:gap-3 hover:opacity-90 transition-opacity group"
          title="Início"
        >
          <img src={ASSETS.logoIcon} alt="Logo" className="h-7 w-auto" />
          <img src={ASSETS.logoName} alt="RDA" className="w-auto mt-0.5 h-[12px]" />
        </button>
      </div>
      
      {/* MENU SCROLLABLE */}
      <nav className="flex-1 overflow-y-auto overscroll-contain py-3 px-3 custom-scrollbar relative space-y-4">
        {sections.map((section) => {
          const pathname = typeof window !== 'undefined' ? String(window.location?.pathname || '') : '';
          const search = typeof window !== 'undefined' ? String(window.location?.search || '') : '';

          const academyPathActive = pathname === '/academy' || pathname.startsWith('/academy/');
          const academyTabsActive = activeTab === 'academy_admin' || activeTab === 'academy_research';
          const notAdminMode = !search.includes('page=client');

          const showGold = section.key === 'academia' && academyPathActive && academyTabsActive && notAdminMode;

          return (
          <AccordionSection
            key={section.key}
            id={`rda-sidebar-${section.key}`}
            title={section.title}
            iconClass={section.iconClass}
            accent={showGold}
            open={!!openSections[section.key]}
            onToggle={() => toggleSection(section.key)}
          >
            {section.items.map((item) => (
              item?.subItems ? (
                <div key={item.id} className="space-y-1">
                  <MenuItem
                    id={item.id}
                    label={item.label}
                    iconClass={item.iconClass}
                    icon={item.icon}
                    active={activeTab === item.id}
                    onClick={() => toggleItemGroup(item.id)}
                    rightIconClass={openItemGroups[item.id] ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
                    nested
                  />

                  {openItemGroups[item.id] ? (
                    <div className="flex flex-col pl-4 ml-3 border-l border-white/10">
                      {item.subItems.map((sub) => (
                        <MenuItem
                          key={sub.id}
                          id={sub.id}
                          label={sub.label}
                          iconClass={sub.iconClass}
                          icon={sub.icon}
                          badgeCount={
                            sub.id === 'alert_inbox'
                              ? alertInboxCount
                              : 0
                          }
                          active={activeTab === sub.id}
                          onClick={onNavigate}
                          to={`?page=${sub.id}`}
                          nested
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <MenuItem
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  iconClass={item.iconClass}
                  icon={item.icon}
                  active={activeTab === item.id}
                  onClick={onNavigate}
                  to={`?page=${item.id}`}
                  nested
                />
              )
            ))}
          </AccordionSection>
          );
        })}
      </nav>
      
      {/* FOOTER USER */}
      <div className="p-2 border-t border-gray-700/50 bg-black/20 flex-shrink-0 space-y-1.5">
        <button
          onClick={() => window.open('/', '_blank', 'noopener,noreferrer')}
          className="w-full bg-white/5 text-gray-200 border border-white/10 py-1.5 rounded hover:bg-[#C5A059] hover:text-[#20243B] text-[9px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
        >
          <i className="fas fa-globe text-[10px]"></i> Ir para o Site
        </button>

        {isDev ? (
          <button
            type="button"
            disabled={chaosRunning}
            onClick={async () => {
              if (chaosRunning) return;
              setChaosRunning(true);
              try {
                const m = await import('../../utils/chaosMonkey');
                await m.runGlobalChaosTest(setActiveTab);
              } catch (e) {
                console.error('[CHAOS]', e);
              } finally {
                setChaosRunning(false);
              }
            }}
            className={
              'w-full border py-1.5 rounded text-[9px] font-bold uppercase tracking-wide flex items-center justify-center gap-2 ' +
              (chaosRunning
                ? 'bg-red-500/10 text-red-300 border-red-500/20 opacity-70 cursor-not-allowed'
                : 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-600 hover:text-white')
            }
            title="☢️ Chaos Monkey (DEV)"
          >
            <span className="text-[10px]">☢️</span> {chaosRunning ? 'Chaos...' : 'Chaos QA'}
          </button>
        ) : null}

        {isDev ? (
          <button
            type="button"
            onClick={async () => {
              try {
                const m = await import('../../utils/chaosMonkey');
                const list = m.getChaosReports ? m.getChaosReports() : [];
                setChaosReports(Array.isArray(list) ? list : []);
              } catch (e) {
                console.error('[CHAOS][REPORTS]', e);
                setChaosReports([]);
              } finally {
                setChaosReportsOpen(true);
              }
            }}
            className="w-full bg-white/5 text-gray-200 border border-white/10 py-1.5 rounded hover:bg-white/10 hover:text-white text-[9px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
            title="Ver relatórios do Chaos Monkey (DEV)"
          >
            <span className="text-[10px]">📋</span> Relatórios Chaos
          </button>
        ) : null}

        <div className="flex items-center gap-3 px-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center text-white border border-gray-600 shadow-inner">
            <i className="fas fa-user text-[10px]"></i>
          </div>
          <div className="overflow-hidden w-full">
            <p className="text-[9px] font-medium text-white truncate" title={user?.email}>{user?.email}</p>
            <div className="mt-0.5 w-full text-[8px] bg-white/10 text-gray-400 py-0.5 rounded border border-white/10 flex items-center justify-center gap-1 font-bold uppercase tracking-wider cursor-default select-none">
               ADMIN
            </div>
          </div>
        </div>
        <button onClick={logoutUser} className="w-full bg-red-500/10 text-red-400 border border-red-500/20 py-1 rounded hover:bg-red-600 hover:text-white text-[9px] font-bold uppercase tracking-wide flex items-center justify-center gap-2 group">
          <i className="fas fa-sign-out-alt text-[10px]"></i> Sair
        </button>
      </div>

      {isDev && chaosReportsOpen ? (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setChaosReportsOpen(false)} />
          <div className="relative w-full max-w-3xl bg-[#1a1e30] text-white rounded-xl border border-white/10 shadow-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
              <div className="text-xs font-extrabold uppercase tracking-widest">Relatórios – Chaos Monkey (DEV)</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const m = await import('../../utils/chaosMonkey');
                      if (m.clearChaosReports) m.clearChaosReports();
                      const list = m.getChaosReports ? m.getChaosReports() : [];
                      setChaosReports(Array.isArray(list) ? list : []);
                    } catch (e) {
                      console.error('[CHAOS][REPORTS]', e);
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-white/10 bg-white/5 hover:bg-white/10"
                  title="Limpar relatórios"
                >
                  Limpar
                </button>
                <button
                  type="button"
                  onClick={() => setChaosReportsOpen(false)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-white/10 bg-white/5 hover:bg-white/10"
                >
                  Fechar
                </button>
              </div>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto space-y-2">
              {Array.isArray(chaosReports) && chaosReports.length > 0 ? (
                chaosReports.map((r) => (
                  <div key={r?.id || Math.random()} className="p-3 rounded-lg border border-white/10 bg-black/20">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[11px] font-extrabold truncate" title={String(r?.id || '')}>
                          {String(r?.id || 'report')}
                        </div>
                        <div className="text-[10px] text-gray-300 mt-1">
                          {String(r?.createdAt || '')} | {Number(r?.durationMs || 0)}ms | {r?.ok ? 'OK' : 'FALHA'}
                        </div>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                          {(Array.isArray(r?.results) ? r.results : []).map((t, idx) => (
                            <div key={idx} className="text-[10px] border border-white/10 bg-white/5 rounded px-2 py-1">
                              <div className="font-bold">{String(t?.test || 'Teste')}</div>
                              <div className={t?.ok ? 'text-emerald-300' : 'text-red-300'}>{t?.ok ? 'OK' : 'FAIL'}</div>
                              {t?.detail ? <div className="text-gray-300 truncate" title={String(t.detail)}>{String(t.detail)}</div> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="text-[10px] px-2 py-1 rounded border border-white/10 bg-white/5">
                        {r?.ok ? '✅' : '❌'}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-gray-300">Nenhum relatório encontrado. Rode o Chaos QA primeiro.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </aside>
  );
};
